import * as Api from '@ViewModels';
import * as React from 'react';
import { AutomationStepCardSchedule } from '../../../components/automation/AutomationStepCard/presentation';
import { BEHAVIOR_BRANCH_TRIGGER_TAG } from '../../../components/automation/AutomationStepCard/schedulingConstants';
import type { ParentCaseInfo } from '../types/stepNodes';
import { normalizeBehaviorBranchSchedule } from '../utils/behaviorBranchSchedule';
import { getBehaviorScheduleAnchorDescription } from '../utils/behaviorSchedule';
import { shouldUseAnchoredSchedule } from '../utils/triggerAutomationStepRules';

interface ScheduleEditorProps {
	/**
	 * The current schedule configuration for the step.
	 */
	schedule: Api.IAutomationStepSchedule | null | undefined;
	/**
	 * The automation trigger to determine timing options (e.g., birthday, meeting).
	 */
	trigger: Api.IAutomationTrigger | null | undefined;
	/**
	 * The type of automation step (affects schedule options, e.g., ActionItem has due date).
	 */
	stepType: Api.AutomationStepType;
	/**
	 * Whether the schedule can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether the component is in a busy/loading state.
	 */
	isBusy?: boolean;
	/**
	 * The step being scheduled.
	 */
	step: Api.IAutomationStep;
	/**
	 * Index of the step in the automation (used for unique IDs).
	 */
	index?: number;
	/**
	 * Callback when schedule changes. Called with the new schedule.
	 */
	onScheduleChange: (schedule: Api.IAutomationStepSchedule | null) => void;
	/**
	 * When true, hide controls that would set the schedule to Immediately.
	 */
	disableImmediately?: boolean;
	/**
	 * Optional parent branch metadata, used to anchor schedule text for behavior paths.
	 */
	parentCaseInfo?: ParentCaseInfo;
	/**
	 * Impersonation context for CSMs editing a customer's automation. The schedule
	 * dropdown reads send-window features from this account when present, instead
	 * of the logged-in admin's userSession.account.
	 */
	impersonationContext?: Api.IImpersonationContext;
}

const behaviorBranchScheduleTrigger: Api.ITagAutomationTrigger = {
	_type: Api.AutomationTriggerType.Tag,
	tag: BEHAVIOR_BRANCH_TRIGGER_TAG,
};

/**
 * ScheduleEditor provides inline timing controls for automation steps.
 *
 * Displays:
 * - "This step happens [X days] [before/after/same day as] [trigger] at [time]"
 * - For ActionItem steps: Due date options (relative day or no due date)
 *
 * Wraps the existing AutomationStepCardSchedule component for use in the
 * canvas-based AutomationFlows editor with proper callback handling.
 */
export function ScheduleEditor({
	schedule,
	trigger,
	stepType,
	canEdit,
	isBusy = false,
	step,
	index = 0,
	onScheduleChange,
	disableImmediately = false,
	parentCaseInfo,
	impersonationContext,
}: ScheduleEditorProps) {
	const isBehaviorCaseBranch = parentCaseInfo?.isBehaviorCase === true;
	// Any switch-branch child (behavior OR regular segment) inherits the root anchoring
	// rule — see AutomationTemplatePublisher.cs lines 456 & 514. Apply normalization
	// uniformly; only the anchor-text swap stays gated on isBehaviorCaseBranch below.
	const isSwitchBranchChild = parentCaseInfo != null;
	const normalizedSchedule = React.useMemo(
		() => (isSwitchBranchChild ? normalizeBehaviorBranchSchedule(schedule, trigger, stepType) : schedule),
		[isSwitchBranchChild, schedule, trigger, stepType]
	);
	const behaviorBranchUsesRootAnchor = isBehaviorCaseBranch && shouldUseAnchoredSchedule(trigger);
	const effectiveTrigger =
		isBehaviorCaseBranch && !behaviorBranchUsesRootAnchor ? behaviorBranchScheduleTrigger : trigger;

	// Local schedule state so the UI updates immediately on user interaction,
	// without waiting for the API round-trip that updates the prop.
	const [localSchedule, setLocalSchedule] = React.useState(normalizedSchedule);

	// Sync from prop when it changes (e.g., after API refetch or parent re-render).
	React.useEffect(() => {
		setLocalSchedule(normalizedSchedule);
	}, [normalizedSchedule]);

	const handleRequestSetSchedule = React.useCallback(
		(newSchedule: Api.IAutomationStepSchedule | null) => {
			const nextSchedule = isSwitchBranchChild
				? normalizeBehaviorBranchSchedule(newSchedule, trigger, stepType)
				: newSchedule;
			// Update local state immediately so the schedule UI reflects the change.
			setLocalSchedule(nextSchedule);
			onScheduleChange(nextSchedule);
		},
		[onScheduleChange, isSwitchBranchChild, trigger, stepType]
	);

	const behaviorAnchorDescription = behaviorBranchUsesRootAnchor
		? undefined
		: getBehaviorScheduleAnchorDescription(parentCaseInfo);

	return (
		<AutomationStepCardSchedule
			canEdit={canEdit}
			isBusy={isBusy}
			index={index}
			onRequestSetSchedule={handleRequestSetSchedule}
			schedule={localSchedule || undefined}
			step={step}
			stepType={stepType}
			trigger={effectiveTrigger || undefined}
			triggerAnchorDescription={behaviorAnchorDescription}
			restrictToAfterOrImmediately={isBehaviorCaseBranch && !behaviorBranchUsesRootAnchor}
			impersonationContext={impersonationContext}
			disableImmediately={disableImmediately}
		/>
	);
}
